import { Tooltip, TooltipProps } from '@mui/material';
import React, { ReactNode, useState } from 'react';

interface TooltipHardenedProps extends Omit<TooltipProps, 'disableInteractive' | 'title'> {
  children: React.JSX.Element;
  tooltip?: ReactNode;
  interactive?: boolean;
}

/**
 * Simple wrapper to clean up conditional tooltips code.
 *
 * Wraps an element in a tooltip only when the tooltip node is not undefined.
 * Hardened tooltips are un-interactive by default in contrast to normal Tooltips.
 * Non-interactive tooltips set pointerEvents: none on the popper so the overlay
 * never captures clicks intended for the wrapped element. Additionally, clicking
 * the anchor dismisses the tooltip until the next hover cycle, preventing the
 * tooltip from obscuring interactive children like selects or inputs.
 */
export default function TooltipHardened({
  tooltip,
  children,
  interactive,
  slotProps,
  ...tooltipProps
}: TooltipHardenedProps): React.JSX.Element {
  const [dismissed, setDismissed] = useState(false);

  if (tooltip === undefined) {
    return children;
  }

  const wrappedChildren = interactive
    ? children
    : React.cloneElement(children, {
        onMouseDown: (e: React.MouseEvent) => {
          children.props.onMouseDown?.(e);
          setDismissed(true);
        },
        onMouseLeave: (e: React.MouseEvent) => {
          children.props.onMouseLeave?.(e);
          setDismissed(false);
        },
      });

  return (
    <Tooltip
      {...tooltipProps}
      title={dismissed ? '' : tooltip}
      disableInteractive={!interactive}
      slotProps={{
        ...slotProps,
        ...(!interactive && {
          popper: { ...slotProps?.popper, sx: { pointerEvents: 'none' } },
        }),
      }}
    >
      {wrappedChildren}
    </Tooltip>
  );
}
